<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="900"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar color="indigo">
            <v-icon dark>
              {{ $t(`Pos.OrderPaymentDialog.Toolbar.Icon`) }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t(`Pos.OrderPaymentDialog.Toolbar.Title`) }}
            </div>

            <div class="text-caption grey--text">
              #{{ order?.OrderCode }} - {{ $t(`Pos.OrderPaymentDialog.Toolbar.Subtitle`) }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-progress-linear
          v-if="loading"
          class="pa-0 ma-0"
          color="secondary"
          height="2"
          indeterminate
        />

        <v-divider />

        <v-card-text
          :style="isDarkMode ? 'background-color: #323232;' : ''"
          class="fill-height pa-3"
          style="min-height: 310px;"
        >
          <v-container
            class="pa-0"
            fluid
          >
            <v-row no-gutters>
              <v-col cols="12">
                <order-payment
                  v-if="isVisible"
                  v-model="paymentTransactions"
                  :cart-id="cartId"
                  :customer-id="customerId"
                  :fiscal-document-mark="order?.FiscalDocumentMark"
                  :has-fiscal-document="order?.HasFiscalDocument"
                  :items="items"
                  :order="order"
                  :order-customer-email="customerEmail"
                  :order-id="orderId"
                  :order-total="orderTotalForPayment"
                  :payment-type="paymentType"
                  :receipt="receipt"
                  :receipt-data.sync="selectedFiscalDocData"
                  :receipt-notify-emails.sync="selectedFiscalDocNotifyEmails"
                  :receipt-notify.sync="selectedFiscalDocNotify"
                  :receipt-type.sync="selectedFiscalDocType"
                  :tip-amount="tipAmount"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-spacer />

          <template v-if="cartId">
            <v-btn
              :disabled="loading || addOrderDisabled"
              :loading="loading"
              class="green white--text elevation-0"
              style="min-width: 165px;"
              @click="onAddOrderClick"
            >
              <span class="mr-2">{{ $t('Pos.Pos.Footer.Button.AddOrder') }}</span>
              <v-spacer />
              {{ orderTotalForPayment | currency }}
            </v-btn>
          </template>

          <template v-if="orderId && userIsDelivery">
            <v-btn
              v-if="order?.HasFiscalDocument"
              :disabled="loading || deliveryUserRequestReceiptDisabled"
              :loading="loading"
              class="green white--text elevation-0"
              style="min-width: 165px;"
              @click="onDeliveryUserUpdateReceipt"
            >
              <span>{{ $t('Order.Statuses.Next.Complete') }}</span>
            </v-btn>

            <v-btn
              v-else
              :disabled="loading || deliveryUserRequestReceiptDisabled"
              :loading="loading"
              class="amber white--text elevation-0"
              style="min-width: 165px;"
              @click="onDeliveryUserRequestReceipt"
            >
              <span>{{ $t('Common.Button.IssueDocument') }}</span>
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import AppData from '@/mixins/appdata'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import OrderPayment from '@/components/payments/orders/OrderPayment.vue'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import Auth from '@/mixins/auth'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import PaymentsCommon from '@/mixins/payments/paymentsCommon'

export default {
  name      : 'OrderPaymentDialog',
  components: {
    OrderPayment
  },
  mixins: [Auth, AppData, PaymentsCommon],
  props : {
    order: {
      type   : Object,
      default: undefined
    },
    orderId: {
      type   : [Number, String],
      default: null
    },
    cartId: {
      type   : [Number, String],
      default: null
    },
    customerId: {
      type   : [Number, String],
      default: null
    },
    customerEmail: {
      type   : String,
      default: ''
    },
    orderTotal: {
      type   : [Number, String],
      default: 0
    },
    transactions: {
      type   : Array,
      default: () => []
    },
    paymentType: {
      type   : String,
      default: PaymentTypeEnum.CASH
    },
    sourceType: {
      type   : [String, Number],
      default: ''
    },
    payload: {
      type   : Object,
      default: undefined
    },
    items: {
      type   : Array,
      default: () => []
    },
    receipt: {
      type   : Boolean,
      default: false
    },
    tipAmount: {
      type   : [String, Number],
      default: 0
    },
    visible: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      loading                      : false,
      selectedFiscalDocType        : FiscalDocTypeEnum.RECEIPT,
      selectedFiscalDocData        : null,
      selectedFiscalDocNotify      : [],
      selectedFiscalDocNotifyEmails: []
    }
  },
  computed: {
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },

    isOrderWithFullDiscount() {
      return this.orderTotal === 0 && this.items?.length > 0
    },

    hasPendingPaymentTransactions() {
      return this.paymentTransactions?.some(transaction => transaction?.WaitingPos || transaction?.busy)
    },

    deliveryUserRequestReceiptDisabled() {
      return this.paymentTransactions?.length <= 0 ||
          !this.order ||
          this.orderBalance !== 0 ||
          this.hasPendingPaymentTransactions ||
          (this.appHasFiscalDocsEnabled && FiscalDocTypeEnum.isInvoice(this.selectedFiscalDocType) && !this.selectedFiscalDocData)
    },

    addOrderDisabled() {
      if (this.isOrderWithFullDiscount && !this.hasAnyTransaction) return false

      return this.paymentTransactions?.length <= 0 ||
          this.orderBalance !== 0 ||
          this.hasPendingPaymentTransactions ||
          (this.hasCashTransaction && !this.cashTransactionAllowed) ||
          (!this.isValidAmountForSelfDeliveryReceipt && FiscalDocTypeEnum.isSelfDoc(this.selectedFiscalDocType)) ||
          (this.hasNoSelfDeliveryTransaction && FiscalDocTypeEnum.isSelfDoc(this.selectedFiscalDocType)) ||
          (this.appHasFiscalDocsEnabled && FiscalDocTypeEnum.isInvoice(this.selectedFiscalDocType) && !this.selectedFiscalDocData)
    },

    orderTotalForPayment() {
      return parseFloat(parseFloat(this.orderTotal).toFixed(2))
    },

    orderBalance() {
      let transactionsTotal = this.paymentTransactions?.reduce((a, b) => a + parseFloat(b.Amount), 0) || 0

      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        transactionsTotal = parseFloat(this.paymentTransactions?.filter(transaction => !this.isEftPosEnabledTransaction(transaction) || (this.isEftPosEnabledTransaction(transaction) && this.eftPosCardTransactionPaidSuccessful(transaction)))?.reduce((a, b) => a + parseFloat(b.Amount), 0).toFixed(2))
      }

      const orderTotal = parseFloat(parseFloat(this.orderTotal).toFixed(2))

      return parseFloat((orderTotal - transactionsTotal).toFixed(2))
    },

    paymentTransactions: {
      get() {
        return this.transactions || []
      },
      set(val) {
        this.transactions = []
      }
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.$bus.$on('add-payment-loader-start', this.startLoader)
        this.$bus.$on('add-payment-loader-stop', this.stopLoader)

        this.selectedFiscalDocType = !this.payload?.autoPrint ? FiscalDocTypeEnum.NONE : FiscalDocTypeEnum.RECEIPT

        if (!this.userIsDelivery && this.appConfig?.LOCATION_DATA?.AllowWithNoDocument && this.appConfig?.LOCATION_DATA?.AllowWithNoDocumentSources?.length && this.appConfig?.LOCATION_DATA?.AllowWithNoDocumentSources?.includes(parseInt(this.sourceType))) {
          this.selectedFiscalDocType = FiscalDocTypeEnum.NONE
        }

        this.selectedFiscalDocNotify = []
        this.selectedFiscalDocNotifyEmails = []

        // Add Order with 100% Discount or 100% Product Discount (Full Discount)
        this.addOrderWithFullDiscount()
      } else {
        this.$bus.$off('add-payment-loader-start', this.startLoader)
        this.$bus.$off('add-payment-loader-stop', this.stopLoader)
        this.$bus.$emit('cart:cart-update', 'order-payment-dialog-close')
        this.onCancelButtonClick()
      }
    }
  },
  methods: {
    addOrderWithFullDiscount() {
      // Add Order with 100% Discount or 100% Product Discount
      if (this.isOrderWithFullDiscount && !this.hasAnyTransaction) {
        this.onAddOrderClick()
      }
    },

    onDeliveryUserUpdateReceipt() {
      this.loading = true

      const payload = {
        Email         : this.selectedFiscalDocNotify?.includes(NotifyTypeEnum.EMAIL) ?? false,
        EmailAddresses: this.selectedFiscalDocNotifyEmails
      }
      this.$bus.$emit('fiscal-document:payment-update', this.order, payload)
    },

    onDeliveryUserRequestReceipt() {
      this.loading = true

      const payload = {
        Company               : this.selectedFiscalDocData,
        Print                 : false,
        Email                 : this.selectedFiscalDocNotify?.includes(NotifyTypeEnum.EMAIL) ?? false,
        EmailAddresses        : this.selectedFiscalDocNotifyEmails,
        DeliveryStaffCompleted: true
      }

      this.$bus.$emit('fiscal-document:create', this.order, this.selectedFiscalDocType, payload)
    },

    onAddOrderClick() {
      this.loading = true

      const payload = {
        ...this.payload,
        receiptType   : this.selectedFiscalDocType,
        receiptData   : this.selectedFiscalDocData,
        printNotify   : this.selectedFiscalDocNotify?.includes(NotifyTypeEnum.PRINT) ?? null,
        emailNotify   : this.selectedFiscalDocNotify?.includes(NotifyTypeEnum.EMAIL) ?? null,
        emailAddresses: this.selectedFiscalDocNotifyEmails,
        isFullDiscount: this.isOrderWithFullDiscount
      }

      if (this.$route.name === 'Pos') {
        this.$bus.$emit('cart:complete-order-after-payment', payload)
        this.onCancelButtonClick()
      }
    },

    onCancelButtonClick() {
      this.isVisible = false
      this.loading = false
      this.selectedFiscalDocType = FiscalDocTypeEnum.RECEIPT
      this.selectedFiscalDocData = null
      this.selectedFiscalDocNotify = []
      this.selectedFiscalDocNotifyEmails = []
    },

    startLoader() {
      this.$set(this, 'loading', true)
    },
    stopLoader() {
      this.$set(this, 'loading', false)
    }
  }
}

</script>

<style scoped>

</style>
